import { formatCondorApiV1Config } from './condor-api-v1';
import { formatPhxApiV2Config } from './phx-api-v2';
import { formatPhxLambdaV1Config, normalizePhxLambdaV1Config } from './phx-lambda-v1';
import { formatValkyrieApiV1Config } from './valkyrie-api-v1';
import {
  formatValkyrieLambdaV1Config,
  normalizeValkyrieLambdaV1Config,
} from './valkyrie-lambda-v1';
import { defaultTemplateName, resolveConfigTarget } from '../projects';
import type { CondorApiV1Config } from '../types/condor-api-v1';
import type { PhxApiV2Config } from '../types/phx-api-v2';
import type { PhxLambdaV1Config } from '../types/phx-lambda-v1';
import type { ValkyrieApiV1Config } from '../types/valkyrie-api-v1';
import type { ValkyrieLambdaV1Config } from '../types/valkyrie-lambda-v1';

type AnyConfig =
  | PhxApiV2Config
  | ValkyrieApiV1Config
  | CondorApiV1Config
  | PhxLambdaV1Config
  | ValkyrieLambdaV1Config;

/**
 * Factory function that selects the appropriate config handler based on templateName.
 * - phx-api-v2: Uses phx-api-v2 handler (includes defaults, normalizes all fields)
 * - valkyrie-api-v1: Uses valkyrie-api-v1 handler (endpoint lambdas, SQS queues, queue consumers)
 * - condor-api-v1: Endpoint lambdas only; strips SQS, authorizers, and defaults.env_vars from output
 * - phx-lambda-v1: A single standalone Lambda per config.json (terraform-aws-lambda v5 schema)
 * - valkyrie-lambda-v1: A single standalone Valkyrie Lambda per config.json
 */
export function formatConfigForTemplate(config: AnyConfig, templateName?: string): AnyConfig {
  if (templateName === 'condor-api-v1') {
    return formatCondorApiV1Config(config as CondorApiV1Config);
  }
  if (templateName === 'valkyrie-api-v1') {
    return formatValkyrieApiV1Config(config as ValkyrieApiV1Config);
  }
  if (templateName === 'phx-lambda-v1') {
    return formatPhxLambdaV1Config(config as PhxLambdaV1Config);
  }
  if (templateName === 'valkyrie-lambda-v1') {
    return formatValkyrieLambdaV1Config(config as ValkyrieLambdaV1Config);
  }
  return formatPhxApiV2Config(config as PhxApiV2Config);
}

/** Normalize a lambda config.json by its template_name, else by the component's lambda project. */
export function normalizeLambdaConfig(
  raw: unknown,
  componentName: string,
): PhxLambdaV1Config | ValkyrieLambdaV1Config {
  const template =
    (raw as { template_name?: string } | null)?.template_name ??
    defaultTemplateName('lambda', resolveConfigTarget(componentName, 'lambda')?.project.id);
  return template === 'valkyrie-lambda-v1'
    ? normalizeValkyrieLambdaV1Config(raw)
    : normalizePhxLambdaV1Config(raw);
}

export { formatPhxApiV2Config } from './phx-api-v2';
export { formatCondorApiV1Config } from './condor-api-v1';
export { formatValkyrieApiV1Config } from './valkyrie-api-v1';
export { formatPhxLambdaV1Config, normalizePhxLambdaV1Config } from './phx-lambda-v1';
export {
  formatValkyrieLambdaV1Config,
  normalizeValkyrieLambdaV1Config,
} from './valkyrie-lambda-v1';
